import { useEffect, useRef } from 'react';

import type { ChatItem } from '../../../contracts/harness.types';
import type {
  NotePushFieldCandidate,
  NotePushHintsData,
  NotePushRecordedData,
} from '../../../contracts/projects/note-push-mapping.types';
import {
  GET_NOTE_PUSH_HINTS_TOOL_NAME,
  RECORD_NOTE_PUSH_HINTS_TOOL_NAME,
} from '../../harness/timeline/derive-is-computer-use-note-push';
import {
  readComputerUseFieldCandidates,
  readNotePushHintsData,
  readNotePushRecordedData,
} from '../state/note-push-candidates';

interface UseIngestComputerUseNotePushCandidatesArgs {
  items: ChatItem[];
  projectId: string | undefined;
}

/**
 * What the app binds: the note-push candidate store's writes, the workspace they
 * key under, and the stored-placements GET's invalidation. Pass stable values
 * (setters, module-level functions).
 */
export interface NotePushCandidateIngestPorts {
  /** The workspace the store keys under; a change re-runs the ingest pass. */
  workspaceId: string | null;
  ingestHints: (data: NotePushHintsData) => void;
  ingestRecorded: (data: NotePushRecordedData) => void;
  ingestFieldCandidates: (payload: {
    templateId: string;
    appName: string;
    fields: NotePushFieldCandidate[];
  }) => void;
  clearCandidates: () => void;
  /** Invalidate the stored-placements GET for one template in this workspace. */
  invalidateTargets: (templateId: string) => void;
}

/**
 * Header the note-push parent pastes VERBATIM into a run_computer_use_task input
 * when delegating the read-only FIELD-discovery run (BE:
 * `service/tools/registrations/note_push_briefs.py` `render_discovery_block` —
 * `"FIELD DISCOVERY (app: {app_name}) — read-only inspection…"`). Discovery runs
 * are the ONLY computer-use runs that emit note-push `FIELD:` lines, so this is
 * the tightest reliable signal that a computer-use result is attributable to the
 * note-push flow (`item.isComputerUseNotePush` is false on run_computer_use_task — it keys
 * off the hint-tool names). A generic computer-use result that merely contains
 * `FIELD:`-shaped text is therefore not ingested.
 */
const COMPUTER_USE_NOTE_PUSH_DISCOVERY_MARKER = 'FIELD DISCOVERY';

const isComputerUseNotePushDiscoveryTask = (input: Record<string, unknown>): boolean =>
  Object.values(input).some(
    (value) =>
      typeof value === 'string' && value.includes(COMPUTER_USE_NOTE_PUSH_DISCOVERY_MARKER)
  );

/**
 * Headless: feed note-push tool results off the project timeline into the
 * ephemeral candidate store (PR#3), so the template-builder panel's mapping
 * chips have data. Two sources:
 *   - `get_note_push_hints` results → the structured hints payload (self-keyed
 *     by template_id + app_name), which also becomes the "active" mapping.
 *   - `run_computer_use_task` results → parsed FIELD candidates, attributed to
 *     the most-recent active hint (the report carries no template/app; this
 *     assumes one note-push flow at a time).
 *
 * Incremental: each tool_call id is processed once (tracked in a ref), so a new
 * ProjectChat render doesn't re-parse the whole transcript. Resets on project
 * change — the consumer-owned reset the global plain-atom store requires.
 */
export const useIngestComputerUseNotePushCandidatesCore = (
  { items, projectId }: UseIngestComputerUseNotePushCandidatesArgs,
  ports: NotePushCandidateIngestPorts
) => {
  const {
    workspaceId,
    ingestHints,
    ingestRecorded,
    ingestFieldCandidates,
    clearCandidates,
    invalidateTargets,
  } = ports;

  const processedIdsRef = useRef<Set<string>>(new Set());
  const activeHintRef = useRef<{ templateId: string; appName: string } | null>(null);
  const projectRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (projectRef.current !== projectId) {
      projectRef.current = projectId;
      processedIdsRef.current = new Set();
      activeHintRef.current = null;
      clearCandidates();
    }

    for (const item of items) {
      if (item.kind !== 'tool_use') continue;
      if (item.status !== 'complete' || item.result?.is_error) continue;
      if (processedIdsRef.current.has(item.id)) continue;
      // A `complete` status can land a render before the result block does.
      // Only process (and mark processed) once the result has actually arrived,
      // or the late-arriving result would be permanently skipped for this mount.
      if (!item.result) continue;

      if (item.name.toLowerCase() === GET_NOTE_PUSH_HINTS_TOOL_NAME) {
        const data = readNotePushHintsData(item);
        // Mark processed ONLY on a successful parse. A `complete` result whose
        // hints payload hasn't fully materialised (or is malformed this render)
        // returns null; marking it here would permanently skip the later,
        // complete snapshot and starve the mapping. Re-parsing until it lands
        // is idempotent.
        if (data) {
          ingestHints(data);
          activeHintRef.current = {
            templateId: data.template_id,
            appName: data.app_name,
          };
          processedIdsRef.current.add(item.id);
        }
        continue;
      }

      if (item.name.toLowerCase() === RECORD_NOTE_PUSH_HINTS_TOOL_NAME) {
        // The agent's confirmed section→field mapping. Merge it into the store
        // (by heading) so the verification panel + "Reassign field" reflect the
        // recorded targets instead of a stale unmapped state; also seeds the
        // active mapping so a run whose only note-push tool was the record (no
        // prior get_note_push_hints) still opens the panel. Same success-only
        // mark as the hints branch.
        const data = readNotePushRecordedData(item);
        if (data) {
          ingestRecorded(data);
          activeHintRef.current = {
            templateId: data.template_id,
            appName: data.app_name,
          };
          // Invalidate the panel's GET (authoritative) so a MOUNTED verification
          // panel refetches the just-recorded rows instead of the stale pre-record
          // result. Prefix key (workspace+template) → all app scopes for this template.
          invalidateTargets(data.template_id);
          processedIdsRef.current.add(item.id);
        }
        continue;
      }

      if (item.isComputerUse) {
        // Only note-push FIELD-discovery runs feed candidates (see marker above).
        if (isComputerUseNotePushDiscoveryTask(item.input)) {
          // Attribution needs the preceding get_note_push_hints result to have
          // populated the active mapping first. If the discovery result is
          // processed before that hint result lands, `active` is null — defer
          // WITHOUT marking so a later pass revisits this item once the hint
          // appears; otherwise its FIELD rows would be dropped and never re-read.
          const active = activeHintRef.current;
          if (!active) continue;
          const fields = readComputerUseFieldCandidates(item);
          // An empty/unparseable report on a `complete` snapshot is the same
          // late-materialisation class: defer without marking so a later
          // snapshot that carries FIELD lines still ingests, instead of being
          // permanently skipped.
          if (fields.length === 0) continue;
          ingestFieldCandidates({
            templateId: active.templateId,
            appName: active.appName,
            fields,
          });
          // The 422 → route-to-discovery follow-up completes via THIS path (field
          // candidates), not a record — invalidate the GET too so the panel's
          // dataUpdatedAt advances and it leaves the "Finding…" state.
          invalidateTargets(active.templateId);
        }
        // A non-discovery computer-use item (or an ingested discovery one) is
        // terminal — mark it so it isn't re-scanned every render.
        processedIdsRef.current.add(item.id);
      }
    }
  }, [
    items,
    projectId,
    ingestHints,
    ingestRecorded,
    ingestFieldCandidates,
    clearCandidates,
    workspaceId,
    invalidateTargets,
  ]);
};
